import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Fuel, Users, Settings2, MapPin, Calendar } from "lucide-react";

const FALLBACK_IMG = "https://images.pexels.com/photos/33345481/pexels-photo-33345481.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [form, setForm] = useState({
    pickup_location: "", drop_location: "",
    pickup_date: "", return_date: "",
    trip_type: "one_way", notes: "", customer_phone: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/vehicles/${id}`)
      .then(({ data }) => { if (!cancelled) setV(data); })
      .catch((err) => {
        console.error("[vehicle-detail] failed to load:", err?.message || err);
        if (!cancelled) setV(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  const total = (() => {
    if (!v || !form.pickup_date || !form.return_date) return null;
    const d1 = new Date(form.pickup_date); const d2 = new Date(form.return_date);
    const days = Math.max(1, Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) || 1);
    return { days, amount: (v.price_per_day * days).toFixed(2) };
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Please sign in to book"); nav("/login"); return; }
    if (user.role !== "customer") { toast.error("Only customers can book"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        vehicle_id: v.id,
        pickup_location: form.pickup_location,
        drop_location: form.drop_location,
        pickup_date: new Date(form.pickup_date).toISOString(),
        return_date: new Date(form.return_date).toISOString(),
        trip_type: form.trip_type,
        notes: form.notes,
        customer_phone: form.customer_phone || user.phone,
      });
      toast.success(`Booking created · ${data.id.slice(0, 8)}`);
      nav("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (v === null) return <div className="min-h-screen bg-[#050B14] text-slate-400 flex items-center justify-center">Loading…</div>;
  if (v === false) return <div className="min-h-screen bg-[#050B14] text-slate-400 flex items-center justify-center">Vehicle not found</div>;

  return (
    <div className="min-h-screen bg-[#050B14] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 mb-8">
            <img src={v.image_url || FALLBACK_IMG} alt={v.name} className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400 mb-3">{v.brand} · {v.category}</p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-6">{v.name}</h1>
          <p className="text-slate-300 leading-relaxed mb-8 max-w-2xl">{v.description || "Premium ride from a verified vendor."}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <Badge icon={<Users />} label="Seats" value={v.seats} />
            <Badge icon={<Settings2 />} label="Transmission" value={v.transmission} />
            <Badge icon={<Fuel />} label="Fuel" value={v.fuel_type} />
            <Badge icon={<MapPin />} label="Location" value={v.location} />
          </div>

          <div className="bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Vendor</p>
            <p className="text-lg font-medium">{v.vendor_name || "Verified vendor"}</p>
          </div>
        </div>

        {/* Booking widget */}
        <div className="lg:sticky lg:top-24 h-fit">
          <form onSubmit={submit} className="bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-6">
            <div className="flex items-baseline justify-between mb-6">
              <span className="text-3xl font-light tracking-tight text-sky-300">₹{v.price_per_day}</span>
              <span className="text-sm text-slate-500">per day</span>
            </div>

            <div className="space-y-4">
              <Field label="Pickup location" value={form.pickup_location} onChange={(val) => setForm({ ...form, pickup_location: val })} testid="bk-pickup" required />
              <Field label="Drop location" value={form.drop_location} onChange={(val) => setForm({ ...form, drop_location: val })} testid="bk-drop" required />
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Pickup" value={form.pickup_date} onChange={(val) => setForm({ ...form, pickup_date: val })} testid="bk-pickup-date" />
                <DateField label="Return" value={form.return_date} onChange={(val) => setForm({ ...form, return_date: val })} testid="bk-return-date" />
              </div>
              <Field label="Phone (optional)" value={form.customer_phone} onChange={(val) => setForm({ ...form, customer_phone: val })} testid="bk-phone" />
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Notes</Label>
                <Textarea data-testid="bk-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400" />
              </div>
            </div>

            {total && (
              <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-sm">
                <div className="flex justify-between text-slate-400"><span>₹{v.price_per_day} × {total.days} {total.days === 1 ? "day" : "days"}</span><span>₹{total.amount}</span></div>
                <div className="flex justify-between text-base text-white pt-2 border-t border-white/10"><span>Total</span><span className="font-medium text-sky-300">₹{total.amount}</span></div>
              </div>
            )}

            <Button type="submit" disabled={busy} data-testid="bk-submit-btn"
              className="w-full mt-6 bg-sky-400 text-[#050B14] hover:bg-sky-300 font-medium py-6 shadow-[0_0_20px_rgba(56,189,248,0.2)]">
              {busy ? "Reserving…" : "Reserve now"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Badge({ icon, label, value }) {
  return (
    <div className="bg-[#0A111A]/60 border border-white/10 rounded-xl p-3">
      <div className="text-sky-300 mb-1.5">{icon}</div>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-white mt-0.5">{value}</p>
    </div>
  );
}

function Field({ label, value, onChange, testid, required }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      <Input required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400" />
    </div>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      <div className="relative mt-2">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <Input type="datetime-local" required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="bg-[#050B14] border-white/10 text-white pl-10 focus:border-sky-400 [color-scheme:dark]" />
      </div>
    </div>
  );
}
